import {
  Canvas,
  Ellipse,
  FabricImage,
  Line,
  PencilBrush,
  Point,
  Rect,
  Textbox,
  Triangle,
} from 'fabric';
import { getCursor } from './cursors';

export const modes = {
  PENCIL: 'PENCIL',
  LINE: 'LINE',
  RECTANGLE: 'RECTANGLE',
  TRIANGLE: 'TRIANGLE',
  ELLIPSE: 'ELLIPSE',
  ERASER: 'ERASER',
  SELECT: 'SELECT',
  TEXT: 'TEXT',
};

export class Board {
  canvas;
  modes;
  cursorPencil = getCursor('pencil');
  mouseDown = false;
  drawInstance = null;
  drawingSettings;
  canvasConfig = {
    zoom: 1,
    contentJSON: null,
    minZoom: 0.05,
    maxZoom: 9.99,
    viewportTransform: [1, 0, 0, 1, 0, 0],
  };

  constructor(params) {
    if (params) {
      this.drawingSettings = params.drawingSettings;
    }
    this.canvas = this.initCanvas(params?.canvasElement);
    if (!this.canvas) return;

    this.canvas.once('after:render', () => {
      this.applyCanvasConfig(params?.canvasConfig);
    });

    this.modes = modes;
    this.resetZoom();
    this.setDrawingMode(this.drawingSettings.currentMode);
    this.addZoomListeners();
  }

  initCanvas(canvasElement) {
    Canvas.prototype.getItemByAttr = function (attr, name) {
      const objects = this.getObjects();
      for (let i = 0; i < objects.length; i++) {
        if (objects[i][attr] && objects[i][attr] === name) {
          return objects[i];
        }
      }
      return null;
    };

    const element = canvasElement || document.getElementById('canvas');
    if (!element) return null;

    const parentElement = element.parentNode;

    const canvas = new Canvas(element, { perPixelTargetFind: true });

    if (parentElement) {
      this.resizeObserver = this.handleResize(this.resizeCanvas(canvas, parentElement).bind(this));
      this.resizeObserver.observe(parentElement);
    }

    return canvas;
  }

  async applyCanvasConfig(canvasConfig) {
    this.canvasConfig = { ...this.canvasConfig, ...canvasConfig };

    if (this.canvasConfig.zoom) {
      this.canvas.setZoom(this.canvasConfig.zoom);
    }
    if (this.canvasConfig.contentJSON) {
      // fabric v6+ resolves loadFromJSON asynchronously.
      await this.canvas.loadFromJSON(this.canvasConfig.contentJSON);
      if (!this.canvas) return;
    }
    if (this.canvasConfig.viewportTransform) {
      this.canvas.setViewportTransform([...this.canvasConfig.viewportTransform]);
      this.changeZoom({ scale: 1 });
    }
    this.canvas.requestRenderAll();
    this.canvas.fire('config:change');
  }

  addZoomListeners() {
    const canvas = this.canvas;
    const that = this;
    canvas.off('mouse:wheel');

    canvas.on('mouse:wheel', function (opt) {
      opt.e.preventDefault();
      opt.e.stopPropagation();
      if (opt.e.ctrlKey) {
        const delta = opt.e.deltaY;
        const scale = 0.995 ** delta;
        const point = new Point(opt.e.offsetX, opt.e.offsetY);
        that.changeZoom({ point, scale });
      } else {
        const e = opt.e;
        const vpt = [...canvas.viewportTransform];
        vpt[4] -= e.deltaX;
        vpt[5] -= e.deltaY;
        canvas.setViewportTransform(vpt);
        canvas.requestRenderAll();
      }
    });

    this.addPinchZoomListeners();
  }

  /**
   * fabric dropped its own `touch:gesture` event in v6, so pinch-to-zoom is wired
   * straight onto the interaction canvas with native pointer events instead.
   */
  addPinchZoomListeners() {
    const canvas = this.canvas;
    const element = canvas.upperCanvasEl;
    if (!element || this.pinchHandlers) return;

    const pointers = new Map();
    let previousDistance = null;

    const distance = () => {
      const [a, b] = [...pointers.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };

    const midpoint = () => {
      const [a, b] = [...pointers.values()];
      const bounds = element.getBoundingClientRect();
      return new Point((a.x + b.x) / 2 - bounds.left, (a.y + b.y) / 2 - bounds.top);
    };

    const onPointerDown = (e) => {
      if (e.pointerType !== 'touch') return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      previousDistance = pointers.size === 2 ? distance() : null;
    };

    const onPointerMove = (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size !== 2) return;

      e.preventDefault();
      const newDistance = distance();
      if (previousDistance) {
        this.changeZoom({ point: midpoint(), scale: newDistance / previousDistance });
      }
      previousDistance = newDistance;
    };

    const onPointerUp = (e) => {
      pointers.delete(e.pointerId);
      previousDistance = null;
    };

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove, { passive: false });
    element.addEventListener('pointerup', onPointerUp);
    element.addEventListener('pointercancel', onPointerUp);

    this.pinchHandlers = { element, onPointerDown, onPointerMove, onPointerUp };
  }

  removePinchZoomListeners() {
    if (!this.pinchHandlers) return;
    const { element, onPointerDown, onPointerMove, onPointerUp } = this.pinchHandlers;
    element.removeEventListener('pointerdown', onPointerDown);
    element.removeEventListener('pointermove', onPointerMove);
    element.removeEventListener('pointerup', onPointerUp);
    element.removeEventListener('pointercancel', onPointerUp);
    this.pinchHandlers = null;
  }

  setDrawingSettings(drawingSettings) {
    if (!drawingSettings) return;

    this.drawingSettings = { ...this.drawingSettings, ...drawingSettings };
    this.setDrawingMode(this.drawingSettings.currentMode);
  }

  setCanvasConfig(canvasConfig) {
    if (!canvasConfig) return;

    this.applyCanvasConfig(canvasConfig);
  }

  setDrawingMode(mode) {
    this.drawingSettings.currentMode = mode;
    this.resetCanvas();

    switch (mode) {
      case this.modes.PENCIL:
        this.draw();
        break;
      case this.modes.LINE:
        this.createLine();
        break;
      case this.modes.RECTANGLE:
        this.createRect();
        break;
      case this.modes.ELLIPSE:
        this.createEllipse();
        break;
      case this.modes.TRIANGLE:
        this.createTriangle();
        break;
      case this.modes.ERASER:
        this.eraserOn();
        break;
      case this.modes.SELECT:
        this.onSelectMode();
        break;
      case this.modes.TEXT:
        this.createText();
        break;
      default:
        this.draw();
    }
  }

  resetCanvas() {
    const canvas = this.canvas;

    this.removeCanvasListener(canvas);
    canvas.selection = false;
    canvas.isDrawingMode = false;
    canvas.defaultCursor = 'auto';
    canvas.hoverCursor = 'auto';
    canvas.getObjects().forEach((item) => item.set({ selectable: false }));

    if (this.editedTextObject) {
      this.editedTextObject.exitEditing();
      this.editedTextObject = null;
    }
  }

  throttle(f, delay = 300) {
    let timer = 0;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => f.apply(this, args), delay);
    };
  }

  handleResize(callback) {
    return new ResizeObserver(this.throttle(callback, 300));
  }

  resizeCanvas(canvas, whiteboard) {
    return function () {
      if (!this.canvas) return;

      const width = whiteboard.clientWidth;
      const height = whiteboard.clientHeight;

      // Re-entering setDimensions with the size we already have is what turns a
      // stray pixel of overflow into an endless resize/scrollbar loop.
      if (!width || !height) return;
      if (canvas.getWidth() === width && canvas.getHeight() === height) return;

      this.changeZoom({ scale: 1 });
      canvas.setDimensions({ width: width, height: height });
    };
  }

  removeCanvasListener() {
    const canvas = this.canvas;
    canvas.off('mouse:down');
    canvas.off('mouse:move');
    canvas.off('mouse:up');
    canvas.off('mouse:over');
    canvas.off('mouse:out');
  }

  draw() {
    const canvas = this.canvas;
    const drawingSettings = this.drawingSettings;
    canvas.freeDrawingBrush = new PencilBrush(canvas);
    canvas.freeDrawingBrush.width = drawingSettings.brushWidth;
    canvas.freeDrawingBrush.color = drawingSettings.currentColor;
    canvas.isDrawingMode = true;
    canvas.freeDrawingCursor = this.cursorPencil;
  }

  createLine() {
    const canvas = this.canvas;

    canvas.on('mouse:down', this.startAddLine().bind(this));
    canvas.on('mouse:move', this.startDrawingLine().bind(this));
    canvas.on('mouse:up', this.stopDrawing.bind(this));

    canvas.defaultCursor = this.cursorPencil;
    canvas.hoverCursor = this.cursorPencil;
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }

  startAddLine() {
    const canvas = this.canvas;
    const drawingSettings = this.drawingSettings;
    return function ({ e }) {
      this.mouseDown = true;

      const pointer = canvas.getScenePoint(e);
      this.drawInstance = new Line([pointer.x, pointer.y, pointer.x, pointer.y], {
        strokeWidth: drawingSettings.brushWidth,
        stroke: drawingSettings.currentColor,
        selectable: false,
      });

      canvas.add(this.drawInstance);
      canvas.requestRenderAll();
    };
  }

  startDrawingLine() {
    const canvas = this.canvas;
    return function ({ e }) {
      if (this.mouseDown) {
        const pointer = canvas.getScenePoint(e);
        this.drawInstance.set({
          x2: pointer.x,
          y2: pointer.y,
        });
        this.drawInstance.setCoords();
        canvas.requestRenderAll();
      }
    };
  }

  createRect() {
    const canvas = this.canvas;

    canvas.on('mouse:down', this.startAddRect().bind(this));
    canvas.on('mouse:move', this.startDrawingRect().bind(this));
    canvas.on('mouse:up', this.stopDrawing.bind(this));

    canvas.selection = false;
    canvas.defaultCursor = this.cursorPencil;
    canvas.hoverCursor = this.cursorPencil;
    canvas.isDrawingMode = false;
    canvas.getObjects().forEach((item) => item.set({ selectable: false }));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }

  startAddRect() {
    const canvas = this.canvas;
    const drawingSettings = this.drawingSettings;
    return function ({ e }) {
      this.mouseDown = true;

      const pointer = canvas.getScenePoint(e);
      this.origX = pointer.x;
      this.origY = pointer.y;

      this.drawInstance = new Rect({
        stroke: drawingSettings.currentColor,
        strokeWidth: drawingSettings.brushWidth,
        fill: drawingSettings.fill ? drawingSettings.currentColor : 'transparent',
        left: this.origX,
        top: this.origY,
        width: 0,
        height: 0,
        selectable: false,
      });

      canvas.add(this.drawInstance);

      this.drawInstance.on(
        'mousedown',
        function (e) {
          if (drawingSettings.currentMode === this.modes.ERASER) {
            canvas.remove(e.target);
          }
        }.bind(this),
      );
    };
  }

  startDrawingRect() {
    const canvas = this.canvas;
    return function ({ e }) {
      if (this.mouseDown) {
        const pointer = canvas.getScenePoint(e);

        if (pointer.x < this.origX) {
          this.drawInstance.set('left', pointer.x);
        }
        if (pointer.y < this.origY) {
          this.drawInstance.set('top', pointer.y);
        }
        this.drawInstance.set({
          width: Math.abs(pointer.x - this.origX),
          height: Math.abs(pointer.y - this.origY),
        });
        this.drawInstance.setCoords();
        canvas.requestRenderAll();
      }
    };
  }

  stopDrawing() {
    this.mouseDown = false;
  }

  createEllipse() {
    const canvas = this.canvas;

    canvas.on('mouse:down', this.startAddEllipse().bind(this));
    canvas.on('mouse:move', this.startDrawingEllipse().bind(this));
    canvas.on('mouse:up', this.stopDrawing.bind(this));

    canvas.selection = false;
    canvas.defaultCursor = this.cursorPencil;
    canvas.hoverCursor = this.cursorPencil;
    canvas.isDrawingMode = false;
    canvas.getObjects().forEach((item) => item.set({ selectable: false }));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }

  startAddEllipse() {
    const canvas = this.canvas;
    const drawingSettings = this.drawingSettings;
    return function ({ e }) {
      this.mouseDown = true;

      const pointer = canvas.getScenePoint(e);
      this.origX = pointer.x;
      this.origY = pointer.y;
      this.drawInstance = new Ellipse({
        stroke: drawingSettings.currentColor,
        strokeWidth: drawingSettings.brushWidth,
        fill: drawingSettings.fill ? drawingSettings.currentColor : 'transparent',
        left: this.origX,
        top: this.origY,
        cornerSize: 7,
        objectCaching: false,
        selectable: false,
      });

      canvas.add(this.drawInstance);
    };
  }

  startDrawingEllipse() {
    const canvas = this.canvas;

    return function ({ e }) {
      if (this.mouseDown) {
        const pointer = canvas.getScenePoint(e);
        if (pointer.x < this.origX) {
          this.drawInstance.set('left', pointer.x);
        }
        if (pointer.y < this.origY) {
          this.drawInstance.set('top', pointer.y);
        }
        this.drawInstance.set({
          rx: Math.abs(pointer.x - this.origX) / 2,
          ry: Math.abs(pointer.y - this.origY) / 2,
        });
        this.drawInstance.setCoords();
        canvas.requestRenderAll();
      }
    };
  }

  createTriangle() {
    const canvas = this.canvas;

    canvas.on('mouse:down', this.startAddTriangle().bind(this));
    canvas.on('mouse:move', this.startDrawingTriangle().bind(this));
    canvas.on('mouse:up', this.stopDrawing.bind(this));

    canvas.selection = false;
    canvas.defaultCursor = this.cursorPencil;
    canvas.hoverCursor = this.cursorPencil;
    canvas.isDrawingMode = false;
    canvas.getObjects().forEach((item) => item.set({ selectable: false }));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }

  startAddTriangle() {
    const canvas = this.canvas;
    const drawingSettings = this.drawingSettings;
    return function ({ e }) {
      this.mouseDown = true;
      drawingSettings.currentMode = this.modes.TRIANGLE;

      const pointer = canvas.getScenePoint(e);
      this.origX = pointer.x;
      this.origY = pointer.y;
      this.drawInstance = new Triangle({
        stroke: drawingSettings.currentColor,
        strokeWidth: drawingSettings.brushWidth,
        fill: drawingSettings.fill ? drawingSettings.currentColor : 'transparent',
        left: this.origX,
        top: this.origY,
        width: 0,
        height: 0,
        selectable: false,
      });

      canvas.add(this.drawInstance);
    };
  }

  startDrawingTriangle() {
    const canvas = this.canvas;
    return function ({ e }) {
      if (this.mouseDown) {
        const pointer = canvas.getScenePoint(e);
        if (pointer.x < this.origX) {
          this.drawInstance.set('left', pointer.x);
        }
        if (pointer.y < this.origY) {
          this.drawInstance.set('top', pointer.y);
        }
        this.drawInstance.set({
          width: Math.abs(pointer.x - this.origX),
          height: Math.abs(pointer.y - this.origY),
        });

        this.drawInstance.setCoords();
        canvas.requestRenderAll();
      }
    };
  }

  createText() {
    const canvas = this.canvas;
    canvas.isDrawingMode = false;

    canvas.on('mouse:down', (e) => this.addText.call(this, e));
  }

  addText(e) {
    const canvas = this.canvas;
    const drawingSettings = this.drawingSettings;

    const pointer = canvas.getScenePoint(e.e);
    this.origX = pointer.x;
    this.origY = pointer.y;
    const text = new Textbox('', {
      left: this.origX - 10,
      top: this.origY - 10,
      fontSize: drawingSettings.brushWidth * 3 + 10,
      fill: drawingSettings.currentColor,
      editable: true,
      perPixelTargetFind: false,
      // Enter commits the textbox instead of inserting a newline.
      keysMap: { ...Textbox.prototype.keysMap, 13: 'exitEditing' },
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.requestRenderAll();

    // A textbox nobody typed into leaves an invisible object and a stray caret behind.
    text.on('editing:exited', () => {
      if (!text.text) {
        canvas.remove(text);
      }
      if (this.editedTextObject === text) {
        this.editedTextObject = null;
      }
      canvas.requestRenderAll();
    });

    // The browser's own mousedown handling runs after fabric dispatches the event and
    // blurs the hidden textarea, so editing has to start once that has settled.
    setTimeout(() => {
      if (this.canvas !== canvas) return;
      text.enterEditing();
      text.hiddenTextarea?.focus();
      canvas.requestRenderAll();
    }, 0);

    this.editedTextObject = text;

    canvas.off('mouse:down');
    canvas.once('mouse:down', (e1) => {
      // fabric may already have closed the editor itself when the click landed.
      if (this.editedTextObject) {
        this.editedTextObject.exitEditing();
        this.editedTextObject = null;
        canvas.once('mouse:down', (e2) => this.addText(e2));
      } else {
        this.addText(e1);
      }
    });
  }

  eraserOn() {
    const canvas = this.canvas;
    canvas.isDrawingMode = false;

    const erase = (event) => {
      if (event.target) {
        canvas.remove(event.target);
      }
    };

    canvas.on('mouse:down', (event) => {
      erase(event);
      canvas.on('mouse:move', erase);
    });

    canvas.on('mouse:up', () => {
      canvas.off('mouse:move', erase);
    });

    canvas.on('mouse:over', (event) => {
      if (event.target) {
        event.target.set({ opacity: 0.2 });
        canvas.requestRenderAll();
      }
    });

    canvas.on('mouse:out', (event) => {
      if (event.target) {
        event.target.set({ opacity: 1 });
        canvas.requestRenderAll();
      }
    });

    canvas.defaultCursor = getCursor('eraser');
    canvas.hoverCursor = getCursor('eraser');
  }

  onSelectMode() {
    const canvas = this.canvas;
    const drawingSettings = this.drawingSettings;
    drawingSettings.currentMode = '';
    canvas.isDrawingMode = false;
    canvas.selection = true;

    canvas.getObjects().forEach((item) => item.set({ selectable: true }));
    canvas.hoverCursor = 'all-scroll';
  }

  clearCanvas() {
    const canvas = this.canvas;
    canvas.remove(...canvas.getObjects());
    canvas.backgroundImage = undefined;
    canvas.requestRenderAll();
  }

  changeZoom({ point, scale }) {
    if (!point) {
      const width = this.canvas.width;
      const height = this.canvas.height;
      point = new Point(width / 2, height / 2);
    }

    const minZoom = this.canvasConfig.minZoom;
    const maxZoom = this.canvasConfig.maxZoom;

    scale = this.canvas.getZoom() * scale;
    scale = scale < minZoom ? minZoom : scale > maxZoom ? maxZoom : scale;

    this.canvas.zoomToPoint(point, scale);
    this.onZoom({ point, scale });
  }

  resetZoom() {
    const width = this.canvas.width;
    const height = this.canvas.height;
    const point = new Point(width / 2, height / 2);
    const scale = 1;
    this.canvas.zoomToPoint(point, scale);
    this.onZoom({ point, scale });
  }

  onZoom(params) {
    this.addZoomListeners();
    this.canvas.fire('zoom:change', params);
  }

  async openPage(page) {
    const canvas = this.canvas;
    const center = canvas.getCenterPoint();

    // fabric v6+ resolves Image.fromURL as a promise rather than a callback.
    const img = await FabricImage.fromURL(page);
    if (!this.canvas) return;

    if (img.width > img.height) {
      img.scaleToWidth(canvas.width);
    } else {
      img.scaleToHeight(canvas.height - 100);
    }

    img.set({
      top: center.y,
      left: center.x,
      originX: 'center',
      originY: 'center',
    });

    canvas.backgroundImage = img;
    canvas.requestRenderAll();
  }

  getCanvasContentBoundaries() {
    const canvas = this.canvas;
    const objects = canvas.getObjects();

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    objects.forEach((object) => {
      const objectBoundingBox = object.getBoundingRect();

      minX = Math.min(minX, objectBoundingBox.left);
      minY = Math.min(minY, objectBoundingBox.top);
      maxX = Math.max(maxX, objectBoundingBox.left + objectBoundingBox.width);
      maxY = Math.max(maxY, objectBoundingBox.top + objectBoundingBox.height);
    });

    const width = maxX - minX;
    const height = maxY - minY;
    return { minX, minY, maxX, maxY, width, height };
  }

  removeBoard() {
    this.removePinchZoomListeners();
    this.resizeObserver?.disconnect();
    if (this.canvas) {
      this.canvas.off();
      this.canvas.dispose();
    }
    this.canvas = null;
  }
}
